import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import Loading from '../atoms/Loading';
import Textlink from '../atoms/link/Textlink';
import LineTitle from '../atoms/titles/LineTitle';
import LineTitleGray from '../atoms/titles/LineTitleGray';
import Tag from '../atoms/tag/Tag';
import PlanInfo from '../organisms/PlanInfo';
import {
  makeAdditionalParamsPlanList,
  makeAdditionalParamsPlanDetail,
  makeQueryStringsWithDuplicatedSessionKey,
} from '../../utils/queryParams';
import RoomDetail from '../molecules/RoomDetail';
import { BASENAME } from '../../constants';
import { usePrice } from '../../utils/price';
import PlanningInfo from '../molecules/PlanningInfo';
import { FData } from '../Head';

const FEMALE_TAG_ID = 'female_only';

const selector = (state) => ({
  flight: state.flight,
  hotel: state.hotel,
  planDetail: state.planDetail,
});

const PlanDetail = ({
  handlePlanSelect, selectedPlan, hidePlanListLink, handlePlanListLinkBottom, handlePlanListLink,
}) => {
  const { flight: flightState, hotel: hotelState, planDetail } = useSelector(selector);
  const [ activePhotoIndex, setActivePhotoIndex ] = useState(0);


  const { plans } = planDetail;
  const plan = selectedPlan ? selectedPlan : plans && plans.length && plans[0].plan;

  const { searchedFlightsInput, selectedOutboundFlight, selectedInboundFlight, selectedOutboundDate } = flightState;
  const { searchedHotelsInput } = hotelState;

  // TODO
  // Error Handling

  if (!plan) {
    return <Loading show progressRate={50} />;
  }

  const {
    id: planId,
    name: planName,
    stocks,
    // stockStatus,
    recommendationPoint,
    mealCondition,
    planningImplementation,
    roomType,
    room,
    hotel,
    tags: planTags,
    femaleOnlyPlan,
    commentInfo,
    mealInfo,
    chargesRemarks,
    chargesInfantRemarks,
    earlyCheckInTime,
    lateCheckOutTime,
    omotenashiInfo,
    guidance,
  }= plan;
  const { id: roomTypeId, name: roomTypeName } = roomType;

  const {
    id: hotelId,
    name: hotelName,
    imageURLs,
    detailURL,
    mapURL,
    area,
    // recommendationPoint,
    checkInfo,
    tags: hotelTags,
    message,
    specialMeasures,
  } = hotel;

  const tags = [...hotelTags, ...planTags];
  if (femaleOnlyPlan) {
    const femaleTag = {
      id: FEMALE_TAG_ID,
      name: '女性限定',
      tagGroup: {},
    };

    tags.push(femaleTag);
  }

  const { checkin, checkout } = checkInfo;
  const checkInDisplay = !checkin ? '※詳細は施設へお問合せください。'
    : (earlyCheckInTime && earlyCheckInTime !== checkin)
      ? `${earlyCheckInTime} (通常${checkin})` : checkin;
  const checkOutDisplay = !checkout ? '※詳細は施設へお問合せください。'
    : (lateCheckOutTime && lateCheckOutTime !== checkout)
      ? `${lateCheckOutTime} (通常${checkout})` : checkout;

  const {
    prefectureName,
    districtName,
  } = area;

  const { outbound, people } = searchedFlightsInput;
  const { stayNights } = searchedHotelsInput;

  const paramsPlanList = makeAdditionalParamsPlanList(hotelId);
  const search = makeQueryStringsWithDuplicatedSessionKey(paramsPlanList);
  const planListLink = `${BASENAME}/plan_list?${search}`;

  const handleClickButton = () => {
    if (!plan) {
      return;
    }

    if (handlePlanSelect) {
      return handlePlanSelect(plan);
    }

    const planDetailParams = makeAdditionalParamsPlanDetail(planId, roomTypeId);
    const searchConfirm = makeQueryStringsWithDuplicatedSessionKey(planDetailParams);
    const entryConfirmLink = `${BASENAME}/entry_confirm?${searchConfirm}`;
    window.open(entryConfirmLink, '_blank');
  };

  let totalAmount = 0;
  let adultPrice = 0;
  if (plan) {
    const { totalPrice, pricePerAdult } = usePrice(
      people, selectedOutboundFlight, selectedInboundFlight, [plan], selectedOutboundDate
    );
    totalAmount = totalPrice;
    adultPrice = pricePerAdult;
  }

  const activePhotoURL = imageURLs && imageURLs.length && imageURLs[activePhotoIndex];

  // workaround https://gitlab.fqsv.net/dp-plus/front/issues/104 cant use <a>tag
  const onClickDetailLink = () => window.open(detailURL, '_blank');

  const isWDS = plan.tieupCompCD === 'WDS';

  return (
    <div className="pagePlanDetail">
      <FData action="detail" flightState={flightState} plnaDetailState={planDetail} />
      <header className="header">
        <div className="headerTop">
          <div className="headerTopItems">
            <div className="headerTopLeft">
              <h1 className="h1">{hotelName}</h1>
              <div className="area isPC">{prefectureName}／{districtName}</div>
              <div className="isSP facilitySup">
                <div className="facilitySupArea">{prefectureName}／{districtName}</div>
                <div className="facilitySupInfoItems">
                  <div className="facilitySupInfoItem">
                    <Textlink onClick={onClickDetailLink}>施設詳細</Textlink>
                  </div>
                </div>
              </div>
            </div>
            <div className="headerTopRight isPC">
              <Textlink onClick={onClickDetailLink}>施設詳細</Textlink>
            </div>
          </div>
          {specialMeasures && (
            <div>
              <link href="https://ecwww9.nta.co.jp/test_search/style.css" rel="stylesheet" media="all" />
              <ul className="pr_label">
                <li className="pr_icon_01">{specialMeasures}</li>
              </ul>
            </div>
          )}
          <div className="headerTopItems positionPointSP">
            <div className="headerTopLeft">
              <LineTitle>
                <span className="courseName">{planName}</span>
                <br className="isSP" />
                {/* https://gitlab.fqsv.net/dp-plus/front/issues/144 */}
                {/* <span className="courseCode">［コースコード］{plan && plan.id}</span> */}
              </LineTitle>
            </div>
            {!hidePlanListLink && (
              <div className="headerTopRight">
                <Textlink
                  onClick={handlePlanListLink}
                  to={handlePlanListLink ? undefined : planListLink}
                >
                  プラン一覧
                </Textlink>
              </div>
            )}
          </div>
        </div>
        <PlanInfo
          people={people}
          stayNights={Number(stayNights)}
          startDate={outbound.date}
          handleSelectButton={handleClickButton}
          totalAmount={totalAmount}
          adultPrice={adultPrice}
          stocks={stocks}
        />
      </header>
      <div className="body">
        <p className="remark" dangerouslySetInnerHTML={{ __html: chargesRemarks }} />
        <p className="remark" dangerouslySetInnerHTML={{ __html: chargesInfantRemarks }} />
        <div className="planDetailThumbnail">
          <LineTitleGray>プラン詳細</LineTitleGray>
          <div className="planDetailThumbnailBody">
            <div className="thumbnail">
              <div className="thumbnailImage">
                <img src={activePhotoURL} alt="" />
              </div>
              <div className="subImages">
                {imageURLs && imageURLs.map((imageURL, index) => (
                  <div
                    key={index}
                    className={`subImage ${index === activePhotoIndex ? 'isActive' : ''}`}
                    onClick={() => setActivePhotoIndex(index)}
                  >
                    <img src={imageURL} alt="" />
                  </div>
                ))}
              </div>
            </div>
            <div className="texts">
              <div className="planDetailTable">
                <div className="planDetailTableHeader">宿泊条件</div>
                <div className="planDetailTableBody">
                  <div className="planDetailTableBodyItem">
                    <div className="name">食事条件</div>
                    <div className="content">{mealCondition}</div>
                  </div>
                  <div className="planDetailTableBodyItem">
                    <div className="name">チェックイン</div>
                    <div className="content">
                      {checkInDisplay}
                    </div>
                  </div>
                  <div className="planDetailTableBodyItem">
                    <div className="name">チェックアウト</div>
                    <div className="content">
                      {checkOutDisplay}
                    </div>
                  </div>
                  <div className="planDetailTableBodyItem">
                    <div className="name">お部屋</div>
                    <div className="content">{roomTypeName}</div>
                  </div>
                  <div className="planDetailTableBodyItem">
                    <div className="name">定員</div>
                    <div className="content">{room && room.capacity && room.capacity.max}名</div>
                  </div>
                </div>
              </div>
              <div className="planDetailTagItems">
                {tags && tags.map((t) => {
                  const { id, name, tagGroup } = t;
                  let colorType;
                  if (tagGroup.id === '2') {
                    colorType = 'purple';
                  }
                  if (tagGroup.id === '3') {
                    colorType = 'orange';
                  }
                  if (id === FEMALE_TAG_ID) {
                    colorType = 'pink';
                  }

                  return (
                    <div className="tagItem" key={id}>
                      <Tag colorType={colorType}>{name}</Tag>
                    </div>
                  );
                })}
                {mealCondition && (
                  <div className="tagItem" >
                    <Tag colorType='orange'>{mealCondition}</Tag>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="planDetailText">
          {message && (
            <div className="warningText mobileMargin">
              <div className="warningTextTitle">施設に関するご案内・ご注意</div>
              <p dangerouslySetInnerHTML={{ __html: message }}/>
            </div>
          )}
          <div className="planDetailTable" style={{ maxWidth: 'none' }}>
            <div className="planDetailTableBody" style={{ border: 'none' }}>
              <p
                className="text"
                dangerouslySetInnerHTML={{ __html: recommendationPoint }}
              />
            </div>
          </div>
          {!!commentInfo && !!commentInfo.length && commentInfo.map((c, i) => (
            <div key={i} className="planDetailTable" style={{ maxWidth: 'none' }}>
              {!!c.title && (
                <div className="planDetailTableHeader">{c.title}</div>
              )}
              <div className="planDetailTableBody" style={{ borderBottom: 'none'}}>
                <p
                  className="text"
                  dangerouslySetInnerHTML={{ __html: c.comments }}
                />
              </div>
            </div>
          ))}
          {!!(mealInfo && !!mealInfo.length) && (
            <div className="planDetailTable" style={{ maxWidth: 'none' }}>
              {mealInfo.map((m, i) => (
                <div key={i} className="planDetailTableBody" style={{ border: 'none' }}>
                  <div className="text">
                    {`●${getMealDisplayFromMealCD(m.mealCD)}`}<br/>
                    {`【場所】${m.place}`}<br/>
                    【内容・備考】<br/>
                    <p className="withIndent" dangerouslySetInnerHTML={{ __html: m.info }}/><br/>
                    <p className="withIndent" dangerouslySetInnerHTML={{ __html: m.remarks }}/>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!!omotenashiInfo && !!omotenashiInfo.length && omotenashiInfo.map((o, i) => (
            <div key={i} className="planDetailTable" style={{ maxWidth: 'none' }}>
              <div className="planDetailTableHeader">{o.title}</div>
              <div className="planDetailTableBody" style={{ borderBottom: 'none'}}>
                <p
                  className="text"
                  dangerouslySetInnerHTML={{ __html: o.contents }}
                />
              </div>
            </div>
          ))}
          {!isWDS && (
            <RoomDetail room={room} roomType={roomType} />
          )}
          <PlanInfo
            people={people}
            stayNights={Number(stayNights)}
            startDate={outbound.date}
            handleSelectButton={handleClickButton}
            totalAmount={totalAmount}
            adultPrice={adultPrice}
            stocks={stocks}
          />
        </div>
        {handlePlanListLinkBottom && (
          <div className="planListBottom">
            <Textlink onClick={handlePlanListLinkBottom} className="planListLink">この施設のプラン一覧を見る</Textlink>
          </div>
        )}
        {guidance && (
          <div className="planDetailOther">
            <div className="planDetailTitle">
              <LineTitleGray>共通のご案内</LineTitleGray>
            </div>
            <div className="planDetailOtherBody">
              <div className="planDetailOtherBodyText" dangerouslySetInnerHTML={{ __html: guidance }} />
            </div>
          </div>
        )}
        <PlanningInfo className="planDetailOther" info={planningImplementation} />
      </div>
    </div>
  );
};

PlanDetail.propTypes = {
  handlePlanSelect: PropTypes.func,
  selectedPlan: PropTypes.object,
  hidePlanListLink: PropTypes.bool,
  handlePlanListLinkBottom: PropTypes.func,
  handlePlanListLink: PropTypes.func,
};

export default PlanDetail;

const getMealDisplayFromMealCD = (mealCD) => {
  switch (mealCD) {
  case '1':
    return '朝食';
  case '2':
    return '昼食';
  case '3':
    return '夕食';
  }
};
